// src/firebase/firebase-auth.ts
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  type User,
  type UserCredential
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import './firebase-init';

const auth = getAuth();
const db = getFirestore();
const googleProvider = new GoogleAuthProvider();
const facebookProvider = new FacebookAuthProvider();

interface AdditionalData {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  [key: string]: any;
}

/**
 * Create a new user account with email and password
 */
export const registerWithEmail = async (email: string, password: string, displayName: string): Promise<User> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update profile with display name
    await updateProfile(user, { displayName });

    // Parse display name into first and last name
    const nameParts = displayName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

    // Create user document in Firestore
    await createUserDocument(user, { 
      displayName,
      firstName,
      lastName,
      isProfileComplete: false
    });

    return user;
  } catch (error) {
    throw error;
  }
};

/**
 * Sign in with email and password
 */
export const loginWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

/**
 * Sign in with Google
 */
export const loginWithGoogle = async (): Promise<User> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Check if user document exists, if not create it
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      // Parse display name into first and last name
      const nameParts = user.displayName ? user.displayName.split(' ') : [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      await createUserDocument(user, {
        firstName,
        lastName,
        profileType: 'patient', // Default new Google users to patient type
        isProfileComplete: false
      });
    }

    return user;
  } catch (error) {
    throw error;
  }
};

/**
 * Sign in with Facebook
 */
export const loginWithFacebook = async (): Promise<User> => {
  try {
    const result = await signInWithPopup(auth, facebookProvider);
    const user = result.user;

    // Check if user document exists, if not create it
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      // Parse display name into first and last name
      const nameParts = user.displayName ? user.displayName.split(' ') : [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      await createUserDocument(user, {
        firstName,
        lastName,
        profileType: 'patient', // Default new Facebook users to patient type
        isProfileComplete: false
      });
    }

    return user;
  } catch (error) {
    throw error;
  }
};

/**
 * Create or update user document in Firestore
 */
export const createUserDocument = async (user: User, additionalData: AdditionalData = {}): Promise<any> => {
  if (!user) return;

  const userRef = doc(db, 'users', user.uid);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    const { email, displayName, photoURL } = user;

    try {
      await setDoc(userRef, {
        email,
        displayName: displayName || additionalData.displayName || '',
        firstName: additionalData.firstName || '',
        lastName: additionalData.lastName || '',
        photoURL: photoURL || '',
        role: additionalData.role || 'user', // Default role
        profileType: additionalData.role || 'patient', // Default profile type
        isProfileComplete: additionalData.isProfileComplete || false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error creating user document', error);
      throw error;
    }
  }

  return userRef;
};

/**
 * Sign out the current user
 */
export const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw error;
  }
};

/**
 * Get the current authenticated user
 */
export const getCurrentUser = (): Promise<User | null> => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });
};

/**
 * Check if a user's profile is complete
 */
export const isUserProfileComplete = async (userId: string): Promise<boolean> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.isProfileComplete === true;
    }
    return false;
  } catch (error) {
    console.error('Error checking profile completion:', error);
    return false;
  }
};

/**
 * Auth state observer
 */
export const onAuthStateChange = (callback: (user: User | null) => void): (() => void) => {
  return onAuthStateChanged(auth, callback);
};

export default {
  registerWithEmail,
  loginWithEmail,
  loginWithGoogle,
  loginWithFacebook,
  logout,
  resetPassword,
  getCurrentUser,
  isUserProfileComplete,
  onAuthStateChange
};
